import type { DateRangeValue } from "../calendar";
import type { MonthRangeValue, PeriodMonth } from "../month-range-picker";
import {
  MAX_SAVED_FILTERS,
  SAVED_FILTER_LIMIT_MESSAGE,
  SAVED_FILTER_NAME_DUPLICATE_MESSAGE,
  SAVED_FILTER_NAME_MAX_LENGTH,
  SAVED_FILTER_NAME_REQUIRED_MESSAGE,
  SAVED_FILTER_NAME_TOO_LONG_MESSAGE,
} from "./messages";
import type { FilterDefinition, FilterRow, FilterValue, SavedFilter } from "./types-filter-panel";

/** Só os campos com valor: critérios vazios não fazem parte do filtro. */
export function pruneFilterValue(value: FilterValue): FilterValue {
  return Object.fromEntries(Object.entries(value).filter(([, values]) => values.length > 0));
}

export function hasFilterCriteria(value: FilterValue): boolean {
  return Object.values(value).some((values) => values.length > 0);
}

/** Identidade de um conjunto de critérios (ignora campos vazios e a ordem das chaves). */
export function serializeFilterValue(value: FilterValue): string {
  const pruned = pruneFilterValue(value);
  return JSON.stringify(
    Object.keys(pruned)
      .sort()
      .map((key) => [key, pruned[key]]),
  );
}

/** Uma linha por campo com valor aplicado; sem nenhum critério, uma linha em branco. */
export function buildRows(definitions: FilterDefinition[], value: FilterValue): FilterRow[] {
  const applied = definitions.filter(({ key }) => (value[key] ?? []).length > 0);
  if (applied.length === 0) return [{ id: 0, field: null }];
  return applied.map((definition, index) => ({ id: index, field: definition.key }));
}

/** Linha completa: `month-range` pede início e fim; `date-range` aceita só o início; os demais, ao menos um valor. */
export function isRowComplete(definition: FilterDefinition, values: string[]): boolean {
  if (definition.type === "month-range") return values.length === 2;
  if (definition.type === "date") return values.length === 1 ? values[0] !== "" : values[1] !== "";
  if (definition.type === "text") return (values[0] ?? "").trim() !== "";
  return values.length > 0 && (values[0] ?? "") !== "";
}

/** Dos critérios em construção, só os de linhas completas (o que `Aplicar` emite). */
export function completeFilterValue(
  definitions: FilterDefinition[],
  rows: FilterRow[],
  draft: FilterValue,
): FilterValue {
  const result: FilterValue = {};
  for (const row of rows) {
    const definition = definitions.find(({ key }) => key === row.field);
    const values = row.field ? (draft[row.field] ?? []) : [];
    if (definition && row.field && isRowComplete(definition, values)) result[row.field] = values;
  }
  return result;
}

/** `AAAA-MM-DD` ↔ data local. */
export function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function fromIsoDate(iso: string | undefined): Date | null {
  const match = iso ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso) : null;
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
}

export function dateRangeToValues(range: DateRangeValue): string[] {
  const [start, end] = range;
  return start ? [toIsoDate(start), ...(end ? [toIsoDate(end)] : [])] : [];
}

export function valuesToDateRange(values: string[]): DateRangeValue {
  return [fromIsoDate(values[0]), fromIsoDate(values[1])];
}

/** `AAAA-MM` ↔ `PeriodMonth` (mês de 1 a 12). */
const toIsoMonth = ({ year, month }: PeriodMonth) => `${year}-${String(month).padStart(2, "0")}`;

function fromIsoMonth(iso: string | undefined): PeriodMonth | null {
  const match = iso ? /^(\d{4})-(\d{2})$/.exec(iso) : null;
  return match ? { year: Number(match[1]), month: Number(match[2]) } : null;
}

export function monthRangeToValues(range: MonthRangeValue): string[] {
  return [toIsoMonth(range.from), toIsoMonth(range.to)];
}

/** Sem valor, o seletor abre no mês atual (a linha só conta como completa depois da escolha). */
export function valuesToMonthRange(values: string[], today = new Date()): MonthRangeValue {
  const current = { year: today.getFullYear(), month: today.getMonth() + 1 };
  return { from: fromIsoMonth(values[0]) ?? current, to: fromIsoMonth(values[1]) ?? current };
}

/** Mensagem de erro do nome (obrigatório, até 50 caracteres, único e dentro do limite) ou `null`. */
export function validateSavedFilterName(name: string, existing: SavedFilter[]): string | null {
  const trimmed = name.trim();
  if (trimmed === "") return SAVED_FILTER_NAME_REQUIRED_MESSAGE;
  if (trimmed.length > SAVED_FILTER_NAME_MAX_LENGTH) return SAVED_FILTER_NAME_TOO_LONG_MESSAGE;
  const duplicated = existing.some(
    (filter) => filter.name.toLocaleLowerCase("pt-BR") === trimmed.toLocaleLowerCase("pt-BR"),
  );
  if (duplicated) return SAVED_FILTER_NAME_DUPLICATE_MESSAGE;
  if (existing.length >= MAX_SAVED_FILTERS) return SAVED_FILTER_LIMIT_MESSAGE;
  return null;
}
